import { useMemo } from "react"
import { api } from "~/utils/api"
import { combineOrdersMaps } from "~/utils/orderMapUtils"

type Props = {
  variantIds: string[]
  collectionIds: number[]
  /** Subset of collectionIds that have ended — their future sales orders count toward demand. */
  endedCollectionIds?: number[]
  warehouseId?: string
  channel?: string
  externalCollectionIds?: string[]
  collectionPeriod?: {
    startDate: Date
    endDate: Date
  }
  /** Read demand from the latest published demand plan where available (REKD-665). */
  preferDemandPlan?: boolean
  /** Whether any column these maps feed is on screen. Every read below is collection-wide. */
  enabled?: boolean
}

export type PreOrderMapValue = {
  value: number
  preorders: number
  inboundPreorderAmount: number
  futurePreorders: number
  preOrderAmount: number
}

export type SalesMapValue = {
  sales: number
  salesAmount: number
}

export type IncomingOrdersMapValue = {
  total: number | null
  incoming: number | null
  arrived: number | null
  delayed: number | null
}

export type PurchaseOrdersMapValue = {
  total: number
  draft: number
  sent: number
  registered: number
  delivered: number
}

export type CollectionTableData = {
  preOrderMap: Map<string, PreOrderMapValue>
  forecastMap: Map<string, number>
  budgetAdjustedForecastMap?: Map<string, number>
  salesMap: Map<string, SalesMapValue>
  outMap: Map<string, string[]>
  productionSkuCountMap: Map<string, number>
  closingStockMap: Map<string, number>
  ordersMap: Map<string, number>
  incomingOrdersMap?: Map<string, IncomingOrdersMapValue>
  purchaseOrdersMap?: Map<string, PurchaseOrdersMapValue>
  expectedReturnsMap?: Map<string, number>
  /** Per-variant future sales orders from ended collections only. */
  futureSalesOrdersMap?: Map<string, number>
}

export default function useCollectionTableData({
  variantIds,
  collectionIds,
  endedCollectionIds,
  warehouseId,
  channel,
  externalCollectionIds,
  preferDemandPlan,
  enabled = true,
}: Props) {
  let isLoading = false

  const { data: forecastMap, isPending: forecastIsPending } =
    api.forecasts.getVariantForecastForCollections.useQuery(
      { collectionIds, warehouseId, channel, preferDemandPlan },
      { enabled: enabled && variantIds.length > 0 && collectionIds.length > 0 },
    )

  const { data: preOrderMap, isPending: collectionSalesIsPending } =
    api.preOrder.getPreordersForCollectionByVariants.useQuery(
      { collectionIds, warehouseId, channel },
      { enabled: enabled && variantIds.length > 0 && collectionIds.length > 0 },
    )

  const { data: salesMap, isPending: salesIsPending } =
    api.sales.getSalesForCollectionPeriodOnVariant.useQuery(
      { collectionIds, warehouseId, channel },
      { enabled: enabled && variantIds.length > 0 && collectionIds.length > 0 },
    )

  // Future sales orders scoped to ended collections (demand is 0 there).
  const endedIds = endedCollectionIds ?? []
  const { data: endedPreOrderMap } = api.preOrder.getPreordersForCollectionByVariants.useQuery(
    { collectionIds: endedIds, warehouseId, channel },
    { enabled: enabled && variantIds.length > 0 && endedIds.length > 0 },
  )

  const { data: outData, isPending: outMapIsPending } =
    api.collection.getOutStatusForVariants.useQuery(
      { collectionIds },
      { enabled: enabled && collectionIds.length > 0 },
    )

  const { data: closingStockMap } =
    api.closingStock.getForecastedClosingStockForCollections.useQuery(
      {
        collectionIds,
        warehouseId,
        preferDemandPlan,
      },
      { enabled: enabled && collectionIds.length > 0 },
    )

  const { data: purchaseOrdersMap } =
    api.purchaseOrder.getPurchaseOrdersForCollectionsByVariants.useQuery(
      { collectionIds, warehouseId },
      { enabled: enabled && collectionIds.length > 0 },
    )

  const { data: incomingOrdersMap } =
    api.incomingOrders.getIncomingOrdersForCollectionsByVariants.useQuery(
      { externalCollectionIds: externalCollectionIds ?? [], warehouseId },
      { enabled: enabled && (externalCollectionIds ?? []).length > 0 },
    )

  const { data: expectedReturnsMap } =
    api.expectedReturns.getForecastedReturnsForCollections.useQuery(
      { collectionIds, warehouseId, channel },
      { enabled: enabled && collectionIds.length > 0 },
    )

  const variantsEnabled = enabled && variantIds.length > 0 && collectionIds.length > 0
  isLoading ||=
    variantsEnabled &&
    (collectionSalesIsPending || forecastIsPending || salesIsPending || outMapIsPending)

  const ordersMap = useMemo(
    () => combineOrdersMaps(incomingOrdersMap, purchaseOrdersMap),
    [incomingOrdersMap, purchaseOrdersMap],
  )

  const futureSalesOrdersMap = useMemo(() => {
    const map = new Map<string, number>()
    endedPreOrderMap?.forEach((v, variantId) => map.set(variantId, v.futurePreorders))
    return map
  }, [endedPreOrderMap])

  const data = useMemo(() => {
    return {
      preOrderMap,
      forecastMap,
      salesMap,
      outMap: outData?.outMap,
      productionSkuCountMap: outData?.productionSkuCountMap,
      closingStockMap,
      ordersMap,
      incomingOrdersMap,
      purchaseOrdersMap,
      expectedReturnsMap,
      futureSalesOrdersMap,
    } as CollectionTableData
  }, [
    preOrderMap,
    forecastMap,
    salesMap,
    outData,
    closingStockMap,
    ordersMap,
    incomingOrdersMap,
    purchaseOrdersMap,
    expectedReturnsMap,
    futureSalesOrdersMap,
  ])
  return { data, isLoading }
}
